import { useState, useMemo } from "react";
import { SearchOutlined, DownOutlined, UpOutlined, RiseOutlined, CarOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { Input } from "antd";
import Link from "next/link";
import { pushAutoEvent } from "@/utils/gtag";

function getWaitTag(mins: number) {
  if (mins < 15) return { text: "Short", color: "success" as const };
  if (mins < 25) return { text: "Moderate", color: "processing" as const };
  if (mins < 30) return { text: "Busy", color: "warning" as const };
  return { text: "Very Busy", color: "error" as const };
}

function getWaitBgClass(mins: number) {
  if (mins <= 9) return 'bg-[#ecfdf3] text-[#15803d]';
  if (mins <= 19) return 'bg-[#eff6ff] text-[#3b82f6]';
  if (mins <= 29) return 'bg-[#fffbeb] text-[#d97706]';
  return 'bg-[#fef2f2] text-[#ef4444]';
}

export function AirportDirectory({ airports: allAirports }: { airports: { name: string; code: string; mins: number; region: string }[] }) {
  const [query, setQuery] = useState("");
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());

  const regions = useMemo(
    () => Array.from(new Set(allAirports.map((airport) => airport.region))),
    [allAirports]
  );

  const trending = useMemo(
    () => [...allAirports].sort((a, b) => b.mins - a.mins).slice(0, 5),
    [allAirports]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return allAirports.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.code.toLowerCase().includes(q) ||
        a.region.toLowerCase().includes(q)
    );
  }, [allAirports, query]);

  const toggleRegion = (r: string) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      next.has(r) ? next.delete(r) : next.add(r);
      return next;
    });
  };

  const displayRegions = activeRegion ? [activeRegion] : regions;

  return (
    <section id="airports" className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
      <h2 className="font-display text-lg md:text-xl font-bold text-foreground mb-1 text-center md:text-left">
        Find Your Airport
      </h2>
      <p className="text-xs text-muted-foreground mb-4 text-center md:text-left">
        TSA wait times & parking deals at 50+ U.S. airports
      </p>

      <Input
        placeholder="Search by airport name or code…"
        prefix={<SearchOutlined className="text-muted-foreground" />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        size="large"
        className="mb-5 rounded-xl"
      />

      {filtered !== null ? (
        <div className="mb-6">
          <p className="text-xs text-muted-foreground mb-2">
            {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "{query}"
          </p>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No airports found. Try a different search.</p>
          ) : (
            <ul className="space-y-1.5">
              {filtered.map((a) => (
                <AirportRow key={a.code} airport={a} />
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <div className="mb-6">
            <div className="flex items-center gap-1.5 mb-3">
              <RiseOutlined className="text-destructive" />
              <h3 className="text-sm font-bold text-foreground">Trending — Longest Wait Times</h3>
            </div>
            <div className="flex md:grid md:grid-cols-5 gap-2 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-hide -mx-1 px-1">
              {trending.map((a) => {
                const wait = getWaitTag(a.mins);
                return (
                  <Link
                    key={a.code}
                    href={`/airport-security-wait-times/${a.code.toLowerCase()}`}
                    onClick={() => {
                      pushAutoEvent("tsa", `newTSA-goToTSA-${a.code}`, "click");
                    }}
                    className="snap-start shrink-0 w-[60vw] md:w-auto rounded-xl border border-border bg-white p-3 active:bg-muted/40 md:hover:border-primary/40 md:hover:shadow-sm transition-all group"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-foreground">{a.code}</span>
                      <span className={`${getWaitBgClass(a.mins)} inline-flex w-[64px] items-center justify-center rounded py-0.5 text-[10px] font-medium whitespace-nowrap`}>{a.mins} min</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug truncate">{a.name}</p>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide mb-4">
            <button
              onClick={() => setActiveRegion(null)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeRegion === null
                ? "bg-primary text-white border-primary"
                : "bg-muted/40 text-muted-foreground border-border hover:border-primary/40"
                }`}
            >
              All Regions
            </button>
            {regions.map((r) => (
              <button
                key={r}
                onClick={() => setActiveRegion(activeRegion === r ? null : r)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeRegion === r
                  ? "bg-primary text-white border-primary"
                  : "bg-muted/40 text-muted-foreground border-border hover:border-primary/40"
                  }`}
              >
                {r}
              </button>
            ))}
          </div>

          {displayRegions.map((region) => {
            const airports = allAirports.filter((a) => a.region === region);
            const isExpanded = expandedRegions.has(region) || activeRegion !== null;
            const visibleAirports = isExpanded ? airports : airports.slice(0, 4);

            return (
              <div key={region} className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <EnvironmentOutlined className="text-sm text-muted-foreground" />
                  <h3 className="text-sm font-bold text-foreground">{region}</h3>
                  <span className="text-[10px] text-muted-foreground">({airports.length})</span>
                </div>
                <ul className="space-y-1">
                  {visibleAirports.map((a) => (
                    <AirportRow key={a.code} airport={a} />
                  ))}
                </ul>
                {!isExpanded && airports.length > 4 && (
                  <button
                    onClick={() => toggleRegion(region)}
                    className="mt-1.5 text-xs font-medium text-primary hover:underline flex items-center gap-1"
                  >
                    Show {airports.length - 4} more <DownOutlined className="text-[10px]" />
                  </button>
                )}
                {isExpanded && airports.length > 4 && activeRegion === null && (
                  <button
                    onClick={() => toggleRegion(region)}
                    className="mt-1.5 text-xs font-medium text-muted-foreground hover:underline flex items-center gap-1"
                  >
                    Show less <UpOutlined className="text-[10px]" />
                  </button>
                )}
              </div>
            );
          })}
        </>
      )}
    </section >
  );
}

function AirportRow({ airport }: { airport: { name: string; code: string; mins: number; region: string } }) {
  const wait = getWaitTag(airport.mins);
  return (
    <li>
      <Link
        href={`/airport-security-wait-times/${airport.code.toLowerCase()}`}
        onClick={() => {
          pushAutoEvent("tsa", `newTSA-goToTSA-${airport.code}`, "click");
        }}
        className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-muted/20 border border-border/40 active:bg-muted/50 md:hover:border-primary/30 md:hover:shadow-sm transition-all group min-h-[48px]"
      >
        <div className="min-w-0 flex-1 mr-3">
          <p className="text-sm font-medium text-foreground truncate">
            {airport.name} <span className="text-muted-foreground">({airport.code})</span>
          </p>
        </div>
        <span className={`${getWaitBgClass(airport.mins)} inline-flex w-[64px] items-center justify-center rounded py-0.5 text-[10px] font-medium whitespace-nowrap`}>{airport.mins} min</span>
      </Link>
    </li>
  );
}
